import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Star, Quote } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { testimonials } from "@/content/site";
import { Reveal, SectionHead } from "@/components/apex/primitives";

const Testimonials = () => {
  const { t, isRtl } = useLang();
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);

  const scrollTo = (i) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.children[i];
    if (!card) return;
    /* تمرير أفقي داخل المسار فقط — لا يحرّك الصفحة عموديًا */
    const edge = isRtl ? "right" : "left";
    const delta = card.getBoundingClientRect()[edge] - track.getBoundingClientRect()[edge];
    track.scrollTo({ left: track.scrollLeft + delta, behavior: "smooth" });
  };
  const go = (dir) => scrollTo((active + dir + testimonials.length) % testimonials.length);

  /* تتبّع البطاقة الظاهرة */
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onScroll = () => {
      const cards = Array.from(track.children);
      const edge = isRtl ? "right" : "left";
      const start = track.getBoundingClientRect()[edge];
      let best = 0;
      let bestDist = Infinity;
      cards.forEach((c, i) => {
        const d = Math.abs(c.getBoundingClientRect()[edge] - start);
        if (d < bestDist) {
          bestDist = d;
          best = i;
        }
      });
      setActive(best);
    };
    track.addEventListener("scroll", onScroll, { passive: true });
    return () => track.removeEventListener("scroll", onScroll);
  }, [isRtl]);

  /* تقدّم تلقائي كل ٦ ثوانٍ */
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const id = setInterval(() => {
      const r = trackRef.current?.getBoundingClientRect();
      const visible = r && r.top < window.innerHeight && r.bottom > 0;
      if (visible && document.visibilityState === "visible") go(1);
    }, 6000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const Prev = isRtl ? ChevronRight : ChevronLeft;
  const Next = isRtl ? ChevronLeft : ChevronRight;

  return (
    <section id="testimonials" data-testid="testimonials-section" className="relative bg-[var(--cream-2)] py-14 sm:py-20">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <SectionHead label={t.testimonials.label} title={t.testimonials.title} body={t.testimonials.body} />
          <Reveal delay={0.1}>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => go(-1)}
                aria-label={t.gallery.prev}
                data-testid="testimonials-prev"
                className="grid h-12 w-12 place-items-center rounded-full bg-white text-[var(--text)] transition-colors hover:bg-[var(--yellow)]"
              >
                <Prev className="h-5 w-5" strokeWidth={2} />
              </button>
              <button
                type="button"
                onClick={() => go(1)}
                aria-label={t.gallery.next}
                data-testid="testimonials-next"
                className="grid h-12 w-12 place-items-center rounded-full bg-[var(--charcoal)] text-white transition-colors hover:bg-[var(--yellow)] hover:text-[var(--charcoal)]"
              >
                <Next className="h-5 w-5" strokeWidth={2} />
              </button>
            </div>
          </Reveal>
        </div>

        <div
          ref={trackRef}
          data-testid="testimonials-track"
          className="mt-10 flex snap-x snap-mandatory gap-5 overflow-x-auto pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {testimonials.map((item, i) => (
            <article
              key={item.id}
              data-testid={`testimonial-card-${item.id}`}
              className="ap-card flex w-[86%] shrink-0 snap-start flex-col overflow-hidden sm:w-[48%] lg:w-[32%]"
            >
              <div className="ap-img aspect-[16/10] !rounded-b-none">
                <img
                  src={item.photo}
                  alt={isRtl ? `${item.city_ar} — ${item.name_ar}` : `${item.city_en} — ${item.name_en}`}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover"
                />
                <span className="absolute start-4 top-4 rounded-full bg-white/90 px-3 py-1 text-[11px] font-semibold text-[var(--text)] shadow">
                  {isRtl ? item.city_ar : item.city_en}
                </span>
              </div>
              <div className="flex flex-1 flex-col p-6">
                <div className="flex items-center justify-between">
                  <div className="flex gap-0.5" aria-label={`${item.rating}/5`}>
                    {Array.from({ length: 5 }).map((_, k) => (
                      <Star
                        key={k}
                        className={`h-4 w-4 ${k < item.rating ? "fill-[var(--yellow)] text-[var(--yellow)]" : "text-[var(--line)]"}`}
                      />
                    ))}
                  </div>
                  <Quote className="h-6 w-6 text-[var(--yellow)]" strokeWidth={1.5} />
                </div>
                <p className="mt-4 flex-1 text-[14.5px] leading-[1.9] text-[var(--text)]">
                  {isRtl ? item.text_ar : item.text_en}
                </p>
                <div className="mt-6 flex items-center gap-3 border-t border-[var(--line)] pt-5">
                  <span className="ap-display grid h-11 w-11 place-items-center rounded-full bg-[var(--yellow)] text-[15px] text-[var(--charcoal)]">
                    {(isRtl ? item.name_ar : item.name_en).trim().charAt(0)}
                  </span>
                  <div>
                    <p className="text-[14.5px] font-bold text-[var(--text)]">{isRtl ? item.name_ar : item.name_en}</p>
                    <p className="text-[12.5px] text-[var(--muted)]">{isRtl ? item.city_ar : item.city_en}</p>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-6 flex items-center justify-between gap-4">
          <div className="flex gap-2" data-testid="testimonials-dots">
            {testimonials.map((item, i) => (
              <button
                key={item.id}
                type="button"
                onClick={() => scrollTo(i)}
                aria-label={`${i + 1}`}
                aria-current={active === i}
                className={`h-2.5 rounded-full transition-all duration-500 ${
                  active === i ? "w-8 bg-[var(--charcoal)]" : "w-2.5 bg-[#cfd1d4] hover:bg-[#9a9ea4]"
                }`}
              />
            ))}
          </div>
          <p className="text-[12px] text-[var(--muted-2)]">{t.testimonials.note}</p>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
